'use client';

import { useState } from 'react';
import { BaseTranslations } from '../../../baseTranslations';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { RichText } from '../../../components/RichText';
import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { tr } from '../../../utils/translations/tr';
import LightbulbIcon from '../../icons/LightbulbIcon';
import SingSaverMarkIcon from '../../icons/SingsaverMark';
import { ProductDetailsExpandableList } from '../BaseComponents/ProductDetailsExpandableList';
import { ProductDetailsSectionHeader } from '../BaseComponents/ProductDetailsSectionHeader';
import { ProductDetailsSectionWrapper } from '../BaseComponents/ProductDetailsSectionWrapper';

export const ProductDetailsSectionHighlights: React.FC<
  CmsProduct & { pushDataLayer?: PushDataLayerFn }
> = ({ pushDataLayer, ...product }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const { long_description } = product;

  const quickFacts = product.attributes?.filter(
    (attribute) =>
      attribute.__typename === 'ComponentAttributesAttrKeyValue' &&
      attribute.label === 'Quick Facts',
  );

  const thingsToConsider = product.attributes?.filter(
    (attribute) =>
      attribute.__typename === 'ComponentAttributesAttrKeyValue' &&
      attribute.label !== 'Quick Facts',
  );

  const [expandedFacts, setExpandedFacts] = useState(false);
  const [expandedThings, setExpandedThings] = useState(false);

  const mapAttributesToList = (data?: typeof quickFacts) =>
    data?.reduce<Array<string>>((accum, current) => {
      if (current.__typename === 'ComponentAttributesAttrKeyValue') {
        const values = current.value
          ?.flatMap((i) => i.values?.map((v) => v.value) ?? [])
          .filter((i): i is string => Boolean(i));

        if (values && values.length > 0) {
          accum.push(...values);
        }
      }
      return accum;
    }, []) ?? [];

  const quickFactsList = mapAttributesToList(quickFacts);
  const thingsList = mapAttributesToList(thingsToConsider);

  return (
    <div className="flex flex-col gap-6">
      <ProductDetailsSectionHeader
        icon={<MaterialIcon icon="auto_awesome" size={3} />}
      >
        {tr('pd_nav_highlights', staticText)}
      </ProductDetailsSectionHeader>

      <ProductDetailsSectionWrapper>
        <div className="py-spacing/padding/y px-spacing/padding/x flex flex-col gap-6">
          {quickFactsList.length > 0 && (
            <div>
              <h3 className="alpha-subheading-2 text-text/accent mb-6 mt-0">
                {tr('product_details_quick_facts', staticText)}
              </h3>

              <ProductDetailsExpandableList
                list={quickFactsList}
                expanded={expandedFacts}
                onToggle={() => setExpandedFacts((v) => !v)}
                icon={
                  <SingSaverMarkIcon className="absolute left-0 top-1 size-5 text-fill/secondary" />
                }
                seeMoreText={tr('see_more_pd', staticText)}
                seeLessText={tr('see_less_pd', staticText)}
              />
            </div>
          )}

          {thingsList.length > 0 && (
            <div>
              <h3 className="alpha-subheading-2 text-text/accent mb-6 mt-0">
                {tr('things_to_consider', staticText)}
              </h3>

              <ProductDetailsExpandableList
                list={thingsList}
                expanded={expandedThings}
                onToggle={() => setExpandedThings((v) => !v)}
                icon={
                  <LightbulbIcon className="absolute left-0 top-1 size-5 text-campaign/orange-2" />
                }
                seeMoreText={tr('see_more_pd', staticText)}
                seeLessText={tr('see_less_pd', staticText)}
              />
            </div>
          )}
        </div>
      </ProductDetailsSectionWrapper>

      {long_description && (
        <ProductDetailsSectionWrapper
          dropdownSection={true}
          title={tr('about_this_product', staticText)}
          sectionTitle={tr('pd_nav_highlights', staticText)}
          product={product}
          pushDataLayer={pushDataLayer}
        >
          <div className="py-spacing/padding/y px-spacing/padding/x flex flex-col gap-spacing/gap/x">
            <RichText>{long_description}</RichText>
          </div>
        </ProductDetailsSectionWrapper>
      )}
    </div>
  );
};
